import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronLeft, ShieldAlert } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchPriorityReports, type AdminReport } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A6.02 — Prioritetli navbat (yuqori xavf). GET /admin/reports, faqat yuqori-xavf
 * kategoriyalar (CSAM/zo'ravonlik/firibgarlik/doxxing) + pending. Client-side triage.
 */
export function PriorityQueue() {
  const t = useT()
  const navigate = useNavigate()

  const columns = useMemo<AdminColumn<AdminReport>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      {
        key: 'category',
        header: t.mod.category,
        type: 'enum',
        width: 130,
        options: [
          { value: 'csam', label: 'CSAM', status: 'banned' },
          { value: 'violence', label: t.mod.catViolence, status: 'banned' },
          { value: 'fraud', label: t.mod.catFraud, status: 'review' },
          { value: 'doxxing', label: t.mod.catDoxxing, status: 'review' },
        ],
      },
      {
        key: 'reporter_username',
        header: t.userExtra.by,
        type: 'text',
        width: 130,
        render: (r) =>
          r.reporter_id ? (
            <Link to={`/users/${r.reporter_id}`} className="text-accent hover:underline">
              {r.reporter_username ? `@${r.reporter_username}` : `#${r.reporter_id}`}
            </Link>
          ) : (
            '—'
          ),
      },
      { key: 'reason', header: t.common.reason, type: 'text', minWidth: 200 },
      {
        key: 'id',
        header: t.common.actions,
        type: 'text',
        width: 90,
        sortable: false,
        render: (r) => (
          <Link to={`/moderation/${r.id}`} className="text-accent hover:underline">
            {t.userExtra.openReport}
          </Link>
        ),
      },
      { key: 'created_at', header: t.col.time, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchPriorityReports(params), [])

  return (
    <>
      <PageHeader
        title={t.mod.priorityTitle}
        screenId="A6.02"
        actions={
          <Link
            to="/moderation"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.nav.moderation}
          </Link>
        }
      />
      <DataTable<AdminReport>
        screenId="A6.02"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        onRowClick={(r) => navigate(`/moderation/${r.id}`)}
        emptyState={{ icon: <ShieldAlert size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
